import { EventEmitter, Injectable, Output, Type } from '@angular/core';
import { AbcCore, CompareHelper } from '@barthauer/abc/core';
import { AbcConfigService } from '@barthauer/abc/core/configuration';
import {
  WizardExportConfig,
  WizardExportContentPage,
  WizardExportService,
} from '@barthauer/abc/interfaces/core/wizards/wizard-export';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { GisExportQueryModel, GisExportWizardTransferObject } from './models/export.model';
import { ExportModeSelectionPageComponent } from './pages/export-mode-selection-page/export-mode-selection-page.component';
import { AbcWindowComponent } from '@barthauer/abc/ui-components';
import { ParameterValue } from '@barthauer/abc/queries/parameters/core';
import { WorkerResult } from 'libs/abc/core/data/src/lib/models/worker.model';

@Injectable({
  providedIn: 'root',
})
export class GisExportService {
  @Output() raiseErrorInfo = new EventEmitter();
  private gisExporWorkerUri: string;
  public windowInstance?: AbcWindowComponent;
  public filterPolygon: number[][];

  constructor(
    configService: AbcConfigService,
    private abcCore: AbcCore,
    private wizardExportService: WizardExportService
  ) {
    this.gisExporWorkerUri = configService.configuration.WebApi.ServiceURLs.ExtensionWorkersStartGisExport;
  }

  public loadWizard(
    filterPolygon: number[][],
    parameters: ParameterValue[] = [],
    windowInstance?: AbcWindowComponent,
    activeLayers?: string[]
  ): void {
    this.windowInstance = windowInstance;
    this.filterPolygon = filterPolygon;
    const config: WizardExportConfig = new WizardExportConfig();
    config.transferObject = new GisExportWizardTransferObject();
    config.exportFileNameSchema = 'DXFExport_' + WizardExportConfig.TimeStamp_Placeholder + '.dxf';
    config.welcomePage.title = 'DXF Export-Assistent';
    config.welcomePage.description =
      'Dieser Assistent unterstützt Sie bei der Erstellung von Dateien für die Übergabe von Geometrie- und Sachdaten sowie Darstellungsinformationen an andere Systeme.';

    config.startExportWorker = (transferObject) =>
      this.startGisExportWorker(this.filterPolygon, parameters, activeLayers);

    config.contentPages.push(
      this.createWizardContentPage(
        'Kartenausschnitt',
        'Der Export kann entweder über den sichtbaren Bereich oder einen markierten Bereich erfolgen.',
        ExportModeSelectionPageComponent
      )
    );

    this.wizardExportService.loadWizard(config);
  }

  public minimizeWizardWindow(): void {
    this.windowInstance?.minimizeDialog();
  }

  public maximizeWizardWindow(): void {
    this.windowInstance?.maximizeDialog();
  }

  private createWizardContentPage(
    title: string,
    description: string,
    component: Type<unknown>
  ): WizardExportContentPage {
    const page: WizardExportContentPage = new WizardExportContentPage();
    page.title = title;
    page.description = description;
    page.component = component;

    return page;
  }

  private startGisExportWorker(
    filterPolygon: number[][],
    parameters: ParameterValue[] = [],
    activeLayers: string[] = []
  ): Observable<WorkerResult> {
    const query: GisExportQueryModel = new GisExportQueryModel();
    query.ExteriorPolygon = filterPolygon;
    query.Parameters = parameters;
    query.ActiveLayers = activeLayers;

    return this.abcCore.data.executeWebApiPOSTMethod(this.gisExporWorkerUri, query, true).pipe(
      map((data) => (!CompareHelper.isEmptyObject(data) ? (data as any) : null)),
      map((data) => new WorkerResult(data))
    );
  }
}
